import { activePenSlot } from "./colorSlots";

export type PenStrokeLinecap = "round" | "butt" | "square";
export type PenStrokeDash = "solid" | "dashed" | "dotted";

export type PenSettings = {
  stroke: string;
  strokeWidth: number;
  strokeLinecap: PenStrokeLinecap;
  strokeDash?: PenStrokeDash;
};

const STORAGE_KEY = "wb-pen-settings";

const DEFAULT_SETTINGS: PenSettings = {
  stroke: "#111827",
  strokeWidth: 3,
  strokeLinecap: "round",
  strokeDash: "solid",
};

function isPenStrokeDash(value: unknown): value is PenStrokeDash {
  return value === "solid" || value === "dashed" || value === "dotted";
}

function isPenStrokeLinecap(value: unknown): value is PenStrokeLinecap {
  return value === "round" || value === "butt" || value === "square";
}

/**
 * Настройки пера для только что открытой вкладки.
 *
 * Берём их у той точки палитры, которая была в руке: точки лежат в
 * localStorage и помнят выбор между днями, а настройки пера — в
 * sessionStorage и в новой вкладке пусты. Без этого доска открывалась
 * чёрным пером, хотя выделенной стояла, скажем, розовая точка — цвет на
 * панели и цвет на доске расходились.
 */
function settingsFromActiveSlot(): PenSettings {
  try {
    const slot = activePenSlot();
    return {
      ...DEFAULT_SETTINGS,
      stroke: slot.color,
      strokeWidth: slot.width,
      strokeDash: slot.dash,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function loadSettings(): PenSettings {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return settingsFromActiveSlot();
    const parsed = JSON.parse(raw) as Partial<PenSettings>;
    return {
      stroke: typeof parsed.stroke === "string" ? parsed.stroke : DEFAULT_SETTINGS.stroke,
      strokeWidth:
        typeof parsed.strokeWidth === "number" && Number.isFinite(parsed.strokeWidth)
          ? parsed.strokeWidth
          : DEFAULT_SETTINGS.strokeWidth,
      strokeLinecap: isPenStrokeLinecap(parsed.strokeLinecap)
        ? parsed.strokeLinecap
        : DEFAULT_SETTINGS.strokeLinecap,
      strokeDash: isPenStrokeDash(parsed.strokeDash)
        ? parsed.strokeDash
        : DEFAULT_SETTINGS.strokeDash,
    };
  } catch {
    return settingsFromActiveSlot();
  }
}

function persistSettings(next: PenSettings): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

let settings: PenSettings = loadSettings();

export function getPenSettings(): PenSettings {
  // useSyncExternalStore требует, чтобы getSnapshot возвращал
  // стабильно-идентичный объект, пока данные не менялись.
  return settings;
}

export function setPenSettings(partial: Partial<PenSettings>): void {
  settings = { ...settings, ...partial };
  persistSettings(settings);
}

/** Основные цвета ручки — насыщенные, как маркеры на доске. */
export const PEN_STROKE_PRESETS = [
  "#111827", // почти чёрный
  "#dc2626", // красный
  "#ea580c", // оранжевый
  "#ca8a04", // жёлтый (читаемый на светлой сетке)
  "#16a34a", // зелёный
  "#2563eb", // синий
  "#0891b2", // бирюзовый
  "#9333ea", // фиолетовый
  "#ffffff",
] as const;

export const PEN_WIDTH_PRESETS = [1, 2, 3, 5, 8, 12, 16] as const;

export const PEN_DASH_PRESETS: { id: PenStrokeDash; label: string; title: string }[] = [
  { id: "solid", label: "─", title: "Сплошная" },
  { id: "dashed", label: "┄", title: "Пунктир" },
  { id: "dotted", label: "···", title: "Точки" },
];
